import { useCallback, useEffect, useMemo, useState } from 'react'
import { api, ErroApi, type PacoteCatalogo, type PacoteSessaoItem } from '../lib/api'
import { useSessao } from '../lib/sessao'
import { Secao, Vazio, Carregando, Erro } from '../componentes/ui'
import { Grafico } from '../componentes/Grafico'
import { NovoRegistro } from './NovoRegistro'
import './ficha-paciente.css'

/**
 * A ficha do paciente.
 *
 * Reúne numa tela o que a clínica pergunta na frente do paciente:
 * quem é, o que já foi feito, como a dor está evoluindo, quantas
 * sessões sobram e se tem algo em aberto.
 *
 * O prontuário só carrega para quem tem `prontuario:read` — a
 * recepção NÃO tem, por padrão. E a aba nem aparece: mostrar uma aba
 * que devolve erro ao clicar ensina que o sistema é quebrado, não que
 * o acesso é restrito.
 */

type Ficha = {
  id: string; especie: string; status: string
  dados: Record<string, unknown>
  criado_em: string; assinada_em: string | null
  corrige_id: string | null; modelo: string | null; modelo_slug: string | null
  modelo_versao: number
  assinada_por_nome: string | null
  conselho: string | null; conselho_numero: string | null; conselho_uf: string | null
  /** Já vem formatado do banco (`public.ficha_assinatura`), congelado
      na hora da assinatura. Nulo enquanto a ficha é rascunho. */
  carimbo: string | null
}

type Observacao = {
  chave: string; valor_num: string | null; valor_texto: string | null
  unidade: string | null; sitio: string | null; medido_em: string
  rotulo: string | null; minimo: string | null; maximo: string | null
}

type Pacote = {
  pacote_pac_id: string; nome: string; contratadas: number
  consumidas: number; agendadas: number; a_agendar: number
  status: string; validade: string | null
}

type Recebivel = {
  id: string; descricao: string; bruto_centavos: string
  pago_centavos: string; competencia: string; vencimento: string | null
  status: string; meio: string | null; parcela: number; parcelas: number
}

type Detalhe = {
  id: string; nome: string; nome_social: string | null
  cpf: string | null; telefone: string | null; email: string | null
  nascimento: string | null; observacao: string | null
  financeiro: 'OK' | 'PENDENTE' | 'VENCIDO'
  consentimentos: Array<{
    finalidade: string; texto_versao: string
    concedido_em: string; revogado_em: string | null
  }>
  pacotes: Pacote[]
}

const brl = (c: string | number) =>
  (Number(c) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const data = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('pt-BR') : '—'
const dataHora = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—'

function idade(nascimento: string | null): string {
  if (!nascimento) return ''
  const n = new Date(nascimento), h = new Date()
  let a = h.getFullYear() - n.getFullYear()
  const m = h.getMonth() - n.getMonth()
  if (m < 0 || (m === 0 && h.getDate() < n.getDate())) a--
  return `${a} anos`
}

type Aba = 'resumo' | 'prontuario' | 'financeiro' | 'privacidade'

export function FichaPaciente({ pacienteId, aoVoltar }: {
  pacienteId: string
  aoVoltar(): void
}) {
  const { pode } = useSessao()
  const [aba, setAba] = useState<Aba>('resumo')
  const [p, setP] = useState<Detalhe | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  const verProntuario = pode('prontuario:read')
  const verFinanceiro = pode('financeiro:read')

  /* Extraído do efeito anônimo para a aba de privacidade poder pedir
     recarga depois de conceder ou revogar — o consentimento vem dentro
     do próprio `GET /v1/pacientes/:id`. */
  const carregar = useCallback(async () => {
    try {
      setP(await api<Detalhe>(`/v1/pacientes/${pacienteId}`))
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível carregar.')
    }
  }, [pacienteId])

  useEffect(() => {
    void (async () => {
      try {
        setP(await api<Detalhe>(`/v1/pacientes/${pacienteId}`))
      } catch (e) {
        setErro(e instanceof ErroApi ? e.message : 'Não foi possível carregar.')
      }
    })()
  }, [pacienteId])

  if (erro) {
    return (
      <div className="ficha">
        <button className="botao botao--fantasma botao--pequeno" onClick={aoVoltar}>
          ← Voltar
        </button>
        <div className="aviso aviso--erro" role="alert">{erro}</div>
      </div>
    )
  }

  if (!p) {
    return (
      <div className="ficha">
        <div className="esqueleto" style={{ height: 32, width: 240 }} />
        <div className="esqueleto" style={{ height: 120 }} />
      </div>
    )
  }

  return (
    <div className="ficha">
      <button className="botao botao--fantasma botao--pequeno ficha__voltar" onClick={aoVoltar}>
        ← Pacientes
      </button>

      <header className="ficha__topo">
        <div>
          <h1>{p.nome_social ?? p.nome}</h1>
          <p className="ajuda">
            {p.nome_social && <>Registro: {p.nome} · </>}
            {p.nascimento && <>{data(p.nascimento)} ({idade(p.nascimento)}) · </>}
            {p.telefone ?? 'sem telefone'}
          </p>
        </div>
        <span className={`selo ${
          p.financeiro === 'OK' ? 'selo--ok'
          : p.financeiro === 'VENCIDO' ? 'selo--erro' : 'selo--atencao'}`}>
          {p.financeiro === 'OK' ? 'em dia'
            : p.financeiro === 'VENCIDO' ? 'vencido' : 'pendente'}
        </span>
      </header>

      <nav className="ficha__abas" aria-label="Seções da ficha">
        <button className={aba === 'resumo' ? 'is-ativa' : ''}
                onClick={() => setAba('resumo')}>Resumo</button>
        {/* A aba só existe para quem pode abrir. Mostrar e negar no
            clique ensina que o sistema é quebrado, não que é restrito. */}
        {verProntuario && (
          <button className={aba === 'prontuario' ? 'is-ativa' : ''}
                  onClick={() => setAba('prontuario')}>Prontuário</button>
        )}
        {/* "Privacidade" e não "LGPD": quem trabalha na recepção precisa
            achar isto sem saber a sigla da lei. Sem guarda de escopo: o
            titular tem direito de saber o que se guarda dele, e quem
            atende no balcão é quem colhe o consentimento. */}
        <button className={aba === 'privacidade' ? 'is-ativa' : ''}
                onClick={() => setAba('privacidade')}>Privacidade</button>
        {verFinanceiro && (
          <button className={aba === 'financeiro' ? 'is-ativa' : ''}
                  onClick={() => setAba('financeiro')}>Financeiro</button>
        )}
      </nav>

      {aba === 'resumo' && <AbaResumo p={p} />}
      {aba === 'prontuario' && verProntuario && <AbaProntuario pacienteId={pacienteId} />}
      {aba === 'privacidade' && <AbaPrivacidade pacienteId={pacienteId} p={p} aoMudar={carregar} />}
      {aba === 'financeiro' && verFinanceiro && <AbaFinanceiro pacienteId={pacienteId} />}
    </div>
  )
}

/* ══ resumo ═══════════════════════════════════════════════════════ */

function AbaResumo({ p }: { p: Detalhe }) {
  const lgpd = p.consentimentos.find(
    (c) => c.finalidade === 'LGPD_DADOS' && !c.revogado_em)

  return (
    <div className="ficha__secao">
      {/* Consentimento ausente não é detalhe burocrático: sem ele, o
          tratamento dos dados de saúde não tem base legal. */}
      {!lgpd && (
        <div className="aviso aviso--atencao">
          Este paciente ainda não tem consentimento de tratamento de dados registrado.
        </div>
      )}

      <div className="ficha__cartoes">
        <section className="cartao ficha__cartao">
          <h2>Dados</h2>
          <dl className="ficha__dl">
            <div><dt>CPF</dt><dd className="mono">{p.cpf ?? '—'}</dd></div>
            <div><dt>Telefone</dt><dd className="mono">{p.telefone ?? '—'}</dd></div>
            <div><dt>E-mail</dt><dd>{p.email ?? '—'}</dd></div>
            <div><dt>Nascimento</dt><dd>{data(p.nascimento)}</dd></div>
          </dl>
          {p.observacao && <p className="ficha__obs">{p.observacao}</p>}
        </section>

        <section className="cartao ficha__cartao">
          <h2>Consentimentos</h2>
          {p.consentimentos.length === 0 ? (
            <p className="ajuda">Nenhum registrado.</p>
          ) : (
            <ul className="ficha__lista">
              {p.consentimentos.map((c, i) => (
                <li key={i}>
                  <span className={`selo ${c.revogado_em ? 'selo--neutro' : 'selo--ok'}`}>
                    {c.revogado_em ? 'revogado' : 'ativo'}
                  </span>
                  <span>{c.finalidade.replace(/_/g, ' ').toLowerCase()}</span>
                  <span className="ajuda">{c.texto_versao} · {data(c.concedido_em)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {p.pacotes.length > 0 && (
        <section className="cartao ficha__cartao">
          <h2>Pacotes</h2>
          <ul className="ficha__pacotes">
            {p.pacotes.map((k) => (
              <li key={k.pacote_pac_id}>
                <div className="ficha__pacote-topo">
                  <strong>{k.nome}</strong>
                  <span className="ajuda">
                    {k.consumidas} de {k.contratadas} usadas
                    {k.validade && ` · vale até ${data(k.validade)}`}
                  </span>
                </div>
                {/* A barra responde à pergunta que o paciente faz na
                    recepção: "quantas ainda tenho?" */}
                <div className="barra" role="img"
                     aria-label={`${k.consumidas} de ${k.contratadas} sessões usadas`}>
                  <span className="barra__usado"
                        style={{ width: `${(k.consumidas / k.contratadas) * 100}%` }} />
                  <span className="barra__agendado"
                        style={{ width: `${(k.agendadas / k.contratadas) * 100}%` }} />
                </div>
                <span className="ajuda">
                  {k.a_agendar} a agendar · {k.agendadas} já marcadas
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}

/* ══ prontuário ═══════════════════════════════════════════════════ */

function AbaProntuario({ pacienteId }: { pacienteId: string }) {
  const { estado, pode } = useSessao()
  const podeEscrever = pode('prontuario:write')
  const souProfissional = estado.fase === 'dentro' ? estado.ctx.profissionalId : null
  const carimboOk = estado.fase === 'dentro' ? estado.ctx.podeAssinar : false

  const [fichas, setFichas] = useState<Ficha[]>([])
  const [obs, setObs] = useState<Observacao[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [escrevendo, setEscrevendo] = useState(false)
  const [assinando, setAssinando] = useState<Ficha | null>(null)
  const [adendo, setAdendo] = useState<Ficha | null>(null)
  /* O atendimento a que a evolução se liga.
     Até hoje `ficha.agendamento_id` nascia NULL em 100% dos casos: o
     componente já aceitava o id, `POST /v1/fichas` já gravava, e esta
     tela simplesmente não sabia de agendamento nenhum. O efeito é que
     NENHUMA evolução sabe de qual sessão é — e isso não se reconstrói
     depois. */
  const [sessao, setSessao] = useState<{ id: string; inicio: string; procedimento: string | null } | null>(null)

  const carregar = useCallback(async () => {
    try {
      /* Janela dos últimos 7 dias: a evolução se escreve no dia ou logo
         depois. Buscar o histórico inteiro para achar "a sessão de hoje"
         seria caro e traria escolha errada com mais frequência. */
      const ate = new Date(); ate.setHours(23, 59, 59, 999)
      const de = new Date(ate); de.setDate(de.getDate() - 7); de.setHours(0, 0, 0, 0)
      const janela = `de=${de.toISOString()}&ate=${ate.toISOString()}&paciente_id=${pacienteId}`

      const [f, o, ag] = await Promise.all([
        api<{ itens: Ficha[] }>(`/v1/pacientes/${pacienteId}/fichas`),
        api<{ itens: Observacao[] }>(`/v1/pacientes/${pacienteId}/observacoes`),
        /* Se o filtro ainda não existir na API implantada, a agenda
           voltaria INTEIRA por causa do `removeAdditional` — por isso o
           `.catch` devolve vazio em vez de arriscar ligar a ficha ao
           atendimento de outra pessoa. */
        api<{ agendamentos: Array<{ id: string; inicio: string; status: string; paciente_id: string; procedimento: string | null }> }>(
          `/v1/agenda?${janela}`).catch(() => ({ agendamentos: [] })),
      ])
      setFichas(f.itens); setObs(o.itens)

      const meus = ag.agendamentos
        .filter((a) => a.paciente_id === pacienteId && !a.status.startsWith('CANCELADO'))
        .sort((a, b) => b.inicio.localeCompare(a.inicio))
      setSessao(meus[0] ? { id: meus[0].id, inicio: meus[0].inicio, procedimento: meus[0].procedimento } : null)
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível carregar.')
    } finally { setCarregando(false) }
  }, [pacienteId])
  useEffect(() => { void carregar() }, [carregar])

  /** Agrupa as medições por conceito: cada uma vira um gráfico. */
  const series = useMemo(() => {
    const m = new Map<string, Observacao[]>()
    for (const o of obs) {
      if (o.valor_num == null) continue
      const l = m.get(o.chave) ?? []
      l.push(o); m.set(o.chave, l)
    }
    // Uma medição só não é evolução — é um ponto. Gráfico de um ponto
    // ocupa espaço e não diz nada.
    return [...m.entries()].filter(([, v]) => v.length >= 2)
  }, [obs])

  /** Adendos ficam sob a ficha que corrigem, não soltos na linha do tempo. */
  const { raiz, adendos } = useMemo(() => {
    const ad = new Map<string, Ficha[]>()
    const rz: Ficha[] = []
    for (const f of fichas) {
      if (f.corrige_id) {
        const l = ad.get(f.corrige_id) ?? []
        l.push(f); ad.set(f.corrige_id, l)
      } else rz.push(f)
    }
    return { raiz: rz, adendos: ad }
  }, [fichas])

  if (carregando) return <div className="esqueleto" style={{ height: 200 }} />
  if (erro) return <div className="aviso aviso--erro" role="alert">{erro}</div>

  return (
    <div className="ficha__secao">

      {series.length > 0 && (
        <section className="cartao ficha__cartao">
          <h2>Evolução medida</h2>
          <div className="ficha__graficos">
            {series.map(([chave, pontos]) => (
              <Grafico key={chave}
                       titulo={pontos[0]?.rotulo ?? chave}
                       unidade={pontos[0]?.unidade ?? null}
                       minimo={pontos[0]?.minimo ? Number(pontos[0].minimo) : null}
                       maximo={pontos[0]?.maximo ? Number(pontos[0].maximo) : null}
                       pontos={pontos.map((o) => ({
                         x: new Date(o.medido_em).getTime(),
                         y: Number(o.valor_num),
                       }))} />
            ))}
          </div>
        </section>
      )}

      <section className="cartao ficha__cartao">
        <header className="ficha__cartao-cab">
          <h2>Registros</h2>
          <button className="botao" onClick={() => setEscrevendo(true)}>Escrever</button>
        </header>
        {raiz.length === 0 ? (
          <p className="ajuda">
            Nenhum registro clínico ainda. Toda sessão precisa de uma evolução
            escrita — é ela que sustenta o atendimento numa auditoria.
          </p>
        ) : (
          <ol className="linha-tempo">
            {raiz.map((f) => (
              <li key={f.id} className="linha-tempo__item">
                <header className="linha-tempo__cabeca">
                  <div>
                    <strong>{f.modelo ?? f.especie}</strong>
                    <span className="ajuda"> · {dataHora(f.assinada_em ?? f.criado_em)}</span>
                  </div>
                  <span className={`selo ${
                    f.status === 'ASSINADA' ? 'selo--ok'
                    : f.status === 'ADENDO' ? 'selo--atencao' : 'selo--neutro'}`}>
                    {f.status.toLowerCase()}
                  </span>
                </header>

                {/* `carimbo` vem gravado DENTRO da ficha, congelado no
                    instante da assinatura. Esta linha já foi montada a
                    partir de um JOIN com `profissional`, e mostrava o
                    cadastro de HOJE: bastava corrigir o CREFITO para
                    toda ficha antiga exibir o número novo. */}
                {f.carimbo && (
                  <p className="ajuda">
                    {f.carimbo}
                    {(!f.conselho || f.conselho === 'NENHUM') && (
                      <span className="ajuda--erro"> · sem registro de conselho</span>
                    )}
                  </p>
                )}

                <Dados dados={f.dados} />

                {(adendos.get(f.id) ?? []).map((a) => (
                  <div key={a.id} className="adendo">
                    <header>
                      <span className="selo selo--atencao">adendo</span>
                      <span className="ajuda">{dataHora(a.assinada_em ?? a.criado_em)}</span>
                    </header>
                    {/* O original acima NÃO é alterado nem escondido.
                        Riscar o que estava escrito é o que invalida
                        um prontuário. */}
                    <Dados dados={a.dados} />
                  </div>
                ))}

                {/* ── as duas ações que faltavam ──────────────────────
                    `POST /v1/fichas/:id/assinar` e `/adendo` existem e
                    são testadas desde a semana do prontuário. Nenhuma
                    tela as oferecia: rascunho ficava rascunho para
                    sempre, e o adendo — que esta mesma tela DESENHA — não
                    tinha como ser criado.

                    Assinar só aparece em RASCUNHO; adendo só em ficha já
                    assinada. Ficha assinada é imutável: o adendo é a
                    única correção possível, e ele não apaga nem risca o
                    original — riscar é o que invalida um prontuário. */}
                {podeEscrever && (
                  <div className="linha-tempo__acoes">
                    {f.status === 'RASCUNHO' && (
                      <button className="botao botao--pequeno"
                              onClick={() => setAssinando(f)}>Assinar</button>
                    )}
                    {(f.status === 'ASSINADA' || f.status === 'ADENDO') && (
                      <button className="botao botao--fantasma botao--pequeno"
                              onClick={() => setAdendo(f)}>Escrever adendo</button>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ol>
        )}
      </section>

      {assinando && (
        <AssinarFicha ficha={assinando}
                      aoFechar={() => setAssinando(null)}
                      aoAssinar={async () => { setAssinando(null); await carregar() }} />
      )}

      {adendo && (
        <EscreverAdendo ficha={adendo}
                        aoFechar={() => setAdendo(null)}
                        aoSalvar={async () => { setAdendo(null); await carregar() }} />
      )}

      {escrevendo && (
        <NovoRegistro
          pacienteId={pacienteId}
          {...(sessao ? { agendamentoId: sessao.id } : {})}
          aoFechar={() => setEscrevendo(false)}
          aoSalvar={async () => { setEscrevendo(false); await carregar() }} />
      )}
    </div>
  )
}

function Dados({ dados }: { dados: Record<string, unknown> }) {
  const campos = Object.entries(dados).filter(([k]) => !k.startsWith('_'))
  const motivo = dados._motivo_correcao as string | undefined
  return (
    <>
      {motivo && <p className="adendo__motivo">Motivo: {motivo}</p>}
      <dl className="ficha__dl ficha__dl--largo">
        {campos.map(([k, v]) => (
          <div key={k}>
            <dt>{k.replace(/_/g, ' ')}</dt>
            <dd>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</dd>
          </div>
        ))}
      </dl>
    </>
  )
}

/* ══ financeiro ═══════════════════════════════════════════════════ */

function AbaFinanceiro({ pacienteId }: { pacienteId: string }) {
  const { pode } = useSessao()
  const [itens, setItens] = useState<Recebivel[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    try {
      const r = await api<{ itens: Recebivel[] }>(
        `/v1/recebiveis?paciente_id=${pacienteId}&limite=100`)
      setItens(r.itens)
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível carregar.')
    } finally { setCarregando(false) }
  }, [pacienteId])
  useEffect(() => { void carregar() }, [carregar])

  async function baixar(r: Recebivel, meio: string) {
    try {
      await api(`/v1/recebiveis/${r.id}/baixa`, { metodo: 'POST', corpo: { meio } })
      await carregar()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível dar baixa.')
    }
  }

  const aberto = itens
    .filter((r) => ['ABERTO', 'PARCIAL'].includes(r.status))
    .reduce((s, r) => s + Number(r.bruto_centavos) - Number(r.pago_centavos), 0)

  if (carregando) return <div className="esqueleto" style={{ height: 160 }} />

  return (
    <div className="ficha__secao">
      {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

      {aberto > 0 && (
        <div className="aviso aviso--atencao">
          <strong>{brl(aberto)}</strong> em aberto.
        </div>
      )}

      <Pacotes pacienteId={pacienteId} />

      <section className="cartao ficha__cartao">
        <h2>Lançamentos</h2>
        {itens.length === 0 ? (
          <p className="ajuda">Nada lançado para este paciente.</p>
        ) : (
          <div className="rolagem-x">
            <table className="tabela">
              <thead>
                <tr>
                  <th>Descrição</th><th>Competência</th><th>Valor</th>
                  <th>Situação</th><th></th>
                </tr>
              </thead>
              <tbody>
                {itens.map((r) => (
                  <tr key={r.id}>
                    <td>{r.descricao}</td>
                    <td className="mono">{data(r.competencia)}</td>
                    <td className="mono">{brl(r.bruto_centavos)}</td>
                    <td>
                      <span className={`selo ${
                        r.status === 'LIQUIDADO' ? 'selo--ok'
                        : r.status === 'PAGO' ? 'selo--ok'
                        : r.status === 'PARCIAL' ? 'selo--atencao'
                        : ['CANCELADO','ESTORNADO'].includes(r.status) ? 'selo--neutro'
                        : 'selo--atencao'}`}>
                        {r.status.toLowerCase()}
                      </span>
                      {/* PAGO ≠ LIQUIDADO: o cartão foi cobrado mas o
                          dinheiro só cai em D+30. Dizer isso evita a
                          clínica achar que tem caixa que não tem. */}
                      {r.status === 'PAGO' && (
                        <span className="ajuda"> aguardando compensar</span>
                      )}
                    </td>
                    <td>
                      {pode('financeiro:write') &&
                       ['ABERTO','PARCIAL'].includes(r.status) && (
                        <div className="ficha__baixa">
                          <button className="botao botao--pequeno"
                                  onClick={() => void baixar(r, 'PIX')}>Pix</button>
                          <button className="botao botao--fantasma botao--pequeno"
                                  onClick={() => void baixar(r, 'DINHEIRO')}>Dinheiro</button>
                          <button className="botao botao--fantasma botao--pequeno"
                                  onClick={() => void baixar(r, 'CARTAO_CREDITO')}>Cartão</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}

/* ══ assinar e corrigir ═══════════════════════════════════════════ */

/**
 * Assinar um rascunho.
 *
 * A confirmação existe porque a ação NÃO se desfaz: `ficha_imutavel_trg`
 * trava a linha na mesma transição, e a única correção depois é o
 * adendo — que fica ao lado, sem apagar o original.
 *
 * Mostrar o carimbo ANTES é o ponto: descobrir que saiu sem registro
 * profissional depois de assinar é tarde.
 */
function AssinarFicha({ ficha, aoFechar, aoAssinar }: {
  ficha: Ficha; aoFechar(): void; aoAssinar(): Promise<void>
}) {
  const { estado } = useSessao()
  const eu = estado.fase === 'dentro' ? estado.ctx.profissionalId : null
  const podeCarimbar = estado.fase === 'dentro' ? estado.ctx.podeAssinar : false
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  async function assinar() {
    if (!eu) return
    setSalvando(true); setErro(null)
    try {
      await api(`/v1/fichas/${ficha.id}/assinar`, {
        metodo: 'POST', corpo: { profissional_id: eu },
      })
      await aoAssinar()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível assinar.')
      setSalvando(false)
    }
  }

  return (
    <>
      <div className="painel__fundo" onClick={aoFechar} />
      <div className="porque" role="dialog" aria-modal="true" aria-label="Assinar a ficha">
        <h3>Assinar esta ficha?</h3>
        <p className="ajuda">
          Ficha assinada <strong>não se altera</strong>. Se precisar corrigir depois,
          o caminho é o adendo — que fica ao lado, sem apagar o que estava escrito.
        </p>

        {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

        {!eu && (
          <div className="aviso aviso--atencao" role="note">
            Seu login não está ligado a um profissional. Ligue em
            <strong> Equipe</strong> antes de assinar.
          </div>
        )}
        {eu && !podeCarimbar && (
          <div className="aviso aviso--atencao" role="note">
            Falta o seu registro profissional — <strong>conselho, número e UF</strong>.
            O carimbo é o que dá valor ao documento. Cadastre em
            <strong> Ajustes › Quem trabalha aqui</strong>.
          </div>
        )}

        <div className="porque__acoes">
          <button className="botao botao--fantasma" onClick={aoFechar}>Voltar</button>
          <button className="botao" disabled={!eu || !podeCarimbar || salvando}
                  onClick={() => void assinar()}>
            {salvando ? 'Assinando…' : 'Assinar'}
          </button>
        </div>
      </div>
    </>
  )
}

/**
 * Escrever um adendo.
 *
 * O adendo é a ÚNICA correção de ficha assinada, e por isso pede motivo:
 * quem lê o prontuário depois precisa saber por que existe uma segunda
 * versão. O original continua visível e intocado logo acima.
 */
function EscreverAdendo({ ficha, aoFechar, aoSalvar }: {
  ficha: Ficha; aoFechar(): void; aoSalvar(): Promise<void>
}) {
  const { estado } = useSessao()
  const eu = estado.fase === 'dentro' ? estado.ctx.profissionalId : null
  const podeCarimbar = estado.fase === 'dentro' ? estado.ctx.podeAssinar : false
  const [texto, setTexto] = useState('')
  const [motivo, setMotivo] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  async function salvar() {
    if (!eu || !texto.trim()) return
    setSalvando(true); setErro(null)
    try {
      await api(`/v1/fichas/${ficha.id}/adendo`, {
        metodo: 'POST',
        corpo: {
          profissional_id: eu,
          dados: { correcao: texto.trim() },
          ...(motivo.trim() ? { motivo: motivo.trim() } : {}),
        },
      })
      await aoSalvar()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível gravar o adendo.')
      setSalvando(false)
    }
  }

  return (
    <>
      <div className="painel__fundo" onClick={aoFechar} />
      <div className="porque" role="dialog" aria-modal="true" aria-label="Escrever adendo">
        <h3>Adendo</h3>
        <p className="ajuda">
          O que estava escrito continua lá, inteiro. O adendo entra ao lado, com
          data e carimbo próprios — é assim que prontuário se corrige sem perder
          o que foi registrado na hora.
        </p>

        {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}
        {eu && !podeCarimbar && (
          <div className="aviso aviso--atencao" role="note">
            Falta o seu registro profissional — conselho, número e UF.
          </div>
        )}

        <div className="campo">
          <label htmlFor="ad-texto">A correção</label>
          <textarea id="ad-texto" className="entrada" rows={5} maxLength={4000}
                    placeholder="O que precisa ser corrigido ou acrescentado."
                    value={texto} onChange={(e) => setTexto(e.target.value)} />
        </div>

        <div className="campo">
          <label htmlFor="ad-motivo">Por que (opcional)</label>
          <input id="ad-motivo" className="entrada" maxLength={500}
                 placeholder="Erro de digitação, dado que chegou depois…"
                 value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        </div>

        <div className="porque__acoes">
          <button className="botao botao--fantasma" onClick={aoFechar}>Voltar</button>
          <button className="botao" disabled={!eu || !podeCarimbar || !texto.trim() || salvando}
                  onClick={() => void salvar()}>
            {salvando ? 'Gravando…' : 'Gravar adendo'}
          </button>
        </div>
      </div>
    </>
  )
}

/* ══ privacidade ══════════════════════════════════════════════════ */

/**
 * As finalidades que o banco aceita (`db/0008_prontuario.sql:53`).
 *
 * ⚠️ Esta lista TEM de ser igual ao CHECK. Ela já esteve diferente: a
 * rota declarava cinco valores e só um existia no banco, então quatro
 * morriam em 422 — e `COMUNICACAO_PROMOCIONAL`, a única finalidade que
 * `app.pode_falar` exige para o recall sair, não podia ser gravada por
 * caminho nenhum. Nenhum paciente conseguia ser silenciado.
 */
const FINALIDADES: Array<{ id: string; nome: string; explica: string }> = [
  { id: 'LGPD_DADOS', nome: 'Guardar meus dados',
    explica: 'cadastro, contato e histórico — a base de tudo' },
  { id: 'TRATAMENTO', nome: 'Tratar de mim',
    explica: 'registrar avaliação, evolução e conduta no prontuário' },
  { id: 'COMUNICACAO_TRANSACIONAL', nome: 'Avisos do meu atendimento',
    explica: 'confirmação, lembrete e mudança de horário' },
  { id: 'COMUNICACAO_PROMOCIONAL', nome: 'Convites e campanhas',
    explica: 'retorno, promoção e novidade. Sem isto, o recall não sai.' },
  { id: 'IMAGEM_CLINICA', nome: 'Foto para o tratamento',
    explica: 'registro de evolução, visível só na clínica' },
  { id: 'IMAGEM_MARKETING', nome: 'Foto para divulgação',
    explica: 'redes sociais e site — precisa ser separado do de cima' },
  { id: 'TELESSAUDE', nome: 'Atendimento a distância',
    explica: 'consulta por vídeo e envio de orientação' },
]

type Consentimento = {
  id: string; finalidade: string; texto_versao: string
  concedido_em: string; revogado_em: string | null; expira_em: string | null
}

type Acesso = {
  ocorrido_em: string; evento: string
  quem: string | null; papel: string | null
  ip: string | null; justificativa: string | null
}

/**
 * A aba de privacidade.
 *
 * ─── POR QUE ELA EXISTE ───────────────────────────────────────────
 *
 * `POST /consentimentos`, `/revogar` e `GET /acessos` estavam prontas e
 * sem tela nenhuma. Num software de saúde isso não é uma lacuna de
 * conveniência: consentimento e rastro de acesso são o que a clínica
 * apresenta quando o titular pergunta, e o que a ANPD pede quando algo
 * dá errado.
 *
 * Duas metades, e a segunda é a mais difícil de arranjar depois:
 *  · o que o paciente autorizou, com data e versão do texto;
 *  · quem abriu o prontuário dele, e quando.
 */
function AbaPrivacidade({ pacienteId, p, aoMudar }: {
  pacienteId: string
  p: Detalhe
  aoMudar: () => Promise<void>
}) {
  const { pode } = useSessao()
  const podeEditar = pode('paciente:write')
  const verAcessos = pode('auditoria:read')

  const [acessos, setAcessos] = useState<Acesso[]>([])
  const [carregando, setCarregando] = useState(verAcessos)
  const [erro, setErro] = useState<string | null>(null)
  const [mexendo, setMexendo] = useState<string | null>(null)

  const consentimentos = (p as unknown as { consentimentos?: Consentimento[] }).consentimentos ?? []

  useEffect(() => {
    if (!verAcessos) return
    void (async () => {
      try {
        const r = await api<{ itens: Acesso[] }>(`/v1/pacientes/${pacienteId}/acessos`)
        setAcessos(r.itens)
      } catch { /* sem escopo ou sem rastro: a seção diz isso abaixo */ }
      finally { setCarregando(false) }
    })()
  }, [pacienteId, verAcessos])

  /** O consentimento VIGENTE de uma finalidade, se houver. */
  function vigente(f: string): Consentimento | null {
    const hoje = new Date().toISOString()
    return consentimentos.find((c) =>
      c.finalidade === f && !c.revogado_em && (!c.expira_em || c.expira_em > hoje)) ?? null
  }

  async function conceder(f: string) {
    setMexendo(f); setErro(null)
    try {
      await api(`/v1/pacientes/${pacienteId}/consentimentos`, {
        metodo: 'POST',
        corpo: {
          finalidade: f,
          /* A VERSÃO do texto é o que dá valor ao consentimento: sem
             ela, ninguém sabe a que a pessoa disse sim. Enquanto não
             houver termo versionado na clínica, gravamos a data — que é
             honesto e rastreável. */
          texto_versao: `tela-${new Date().toISOString().slice(0, 10)}`,
          evidencia: { origem: 'balcao', tela: 'ficha-privacidade' },
        },
      })
      await aoMudar()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível registrar.')
    } finally { setMexendo(null) }
  }

  async function revogar(c: Consentimento) {
    setMexendo(c.finalidade); setErro(null)
    try {
      await api(`/v1/pacientes/${pacienteId}/consentimentos/${c.id}/revogar`, {
        metodo: 'POST', corpo: {},
      })
      await aoMudar()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível revogar.')
    } finally { setMexendo(null) }
  }

  return (
    <div className="priv">
      <Erro mensagem={erro} />

      <Secao
        titulo="O que o paciente autorizou"
        descricao="Cada finalidade é uma pergunta separada — juntar todas num sim só é o que a LGPD não aceita."
      >
        <ul className="priv__lista">
          {FINALIDADES.map((f) => {
            const c = vigente(f.id)
            const revogado = consentimentos.find(
              (x) => x.finalidade === f.id && x.revogado_em)
            return (
              <li key={f.id} className={c ? 'is-ok' : ''}>
                <div className="priv__texto">
                  <strong>{f.nome}</strong>
                  <span className="ajuda">{f.explica}</span>
                  {c && (
                    <span className="ajuda">
                      Autorizado em {data(c.concedido_em)} · versão {c.texto_versao}
                      {c.expira_em && ` · vale até ${data(c.expira_em)}`}
                    </span>
                  )}
                  {!c && revogado && (
                    <span className="ajuda ajuda--erro">
                      Revogado em {data(revogado.revogado_em!)}
                    </span>
                  )}
                </div>

                <div className="priv__acao">
                  {c
                    ? <span className="selo selo--ok">autorizado</span>
                    : <span className="selo selo--neutro">não autorizado</span>}
                  {podeEditar && (c
                    ? <button className="botao botao--fantasma botao--pequeno"
                              disabled={mexendo === f.id}
                              onClick={() => void revogar(c)}>
                        {mexendo === f.id ? '…' : 'Revogar'}
                      </button>
                    : <button className="botao botao--pequeno"
                              disabled={mexendo === f.id}
                              onClick={() => void conceder(f.id)}>
                        {mexendo === f.id ? '…' : 'Registrar'}
                      </button>)}
                </div>
              </li>
            )
          })}
        </ul>

        <p className="ajuda priv__nota">
          Revogar não apaga o histórico: o consentimento anterior continua
          registrado com a data em que valeu. Isso é o que prova, depois, que a
          clínica agiu autorizada na época.
        </p>
      </Secao>

      <Secao
        titulo="Quem abriu este prontuário"
        descricao="Todo acesso a dado clínico deixa rastro. É isto que a clínica mostra quando o titular pergunta."
      >
        {!verAcessos ? (
          <p className="ajuda">
            Só quem tem permissão de auditoria vê esta lista. É de propósito: o
            rastro serve para fiscalizar, e quem é fiscalizado não o administra.
          </p>
        ) : carregando ? (
          <Carregando altura={120} />
        ) : acessos.length === 0 ? (
          <Vazio
            mensagem="Nenhum acesso registrado ainda."
            sugestao="A primeira vez que alguém abrir o prontuário deste paciente, aparece aqui."
          />
        ) : (
          <ul className="priv__acessos">
            {acessos.map((a, i) => (
              <li key={`${a.ocorrido_em}-${i}`}>
                <span className="priv__quando mono">{dataHora(a.ocorrido_em)}</span>
                <div className="priv__quem">
                  <strong>{a.quem ?? 'sistema'}</strong>
                  <span className="ajuda">
                    {a.evento}
                    {a.papel && ` · ${a.papel.toLowerCase()}`}
                    {a.justificativa && ` · ${a.justificativa}`}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Secao>
    </div>
  )
}

/* ══ pacote de sessões ════════════════════════════════════════════ */

type PacoteSaldo = {
  pacote_pac_id: string
  nome: string
  contratadas: number | string
  consumidas: number | string
  agendadas: number | string
  a_agendar: number | string
  validade: string | null
  status: string
}

/**
 * Pacote de sessões — dez sessões pagas de uma vez.
 *
 * ─── POR QUE ISTO FALTAVA ─────────────────────────────────────────
 *
 * `GET` e `POST /v1/pacientes/:id/pacotes` existem, e a view
 * `pacote_saldo` já calcula contratadas, consumidas, agendadas e a
 * agendar. Nada disso tinha tela — e pacote é como fisioterapia e
 * pilates vendem, que é a nossa vertical-piloto.
 *
 * Sem a tela, a recepção controlava em papel, e "quantas ainda tenho?"
 * era uma pergunta sem resposta no sistema.
 *
 * ─── A BARRA MOSTRA PROPORÇÃO, NÃO NÚMERO ────────────────────────
 *
 * O número exato está escrito ao lado. A barra existe para o olho pegar
 * de relance quem está acabando — que é a pergunta que a recepção faz
 * de longe, para oferecer a renovação antes da última sessão.
 */
function Pacotes({ pacienteId }: { pacienteId: string }) {
  const { pode } = useSessao()
  const podeEditar = pode('financeiro:write')

  const [itens, setItens] = useState<PacoteSaldo[]>([])
  const [carregando, setCarregando] = useState(true)
  const [novo, setNovo] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    try {
      const r = await api<{ itens: PacoteSaldo[] }>(`/v1/pacientes/${pacienteId}/pacotes`)
      setItens(r.itens)
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : null)
    } finally { setCarregando(false) }
  }, [pacienteId])
  useEffect(() => { void carregar() }, [carregar])

  if (carregando) return null
  if (itens.length === 0 && !podeEditar) return null

  return (
    <>
      <Secao
        titulo="Pacotes"
        descricao="Sessões contratadas de uma vez. A barra mostra quanto já foi usado."
        acao={podeEditar && (
          <button className="botao botao--pequeno" onClick={() => setNovo(true)}>
            Novo pacote
          </button>
        )}
      >
        {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

        {itens.length === 0 ? (
          <Vazio
            mensagem="Nenhum pacote contratado."
            sugestao="Pacote é quando o paciente paga várias sessões de uma vez. O sistema desconta sozinho a cada atendimento concluído."
          />
        ) : (
          <ul className="pac__lista">
            {itens.map((x) => {
              const total = Number(x.contratadas) || 0
              const usadas = Number(x.consumidas) || 0
              const marcadas = Number(x.agendadas) || 0
              const faltam = Number(x.a_agendar) || 0
              const pctUsado = total > 0 ? (usadas / total) * 100 : 0
              const pctMarcado = total > 0 ? (marcadas / total) * 100 : 0
              const acabando = faltam <= 2 && faltam > 0
              return (
                <li key={x.pacote_pac_id}>
                  <div className="pac__cab">
                    <strong>{x.nome}</strong>
                    {x.status !== 'ATIVO'
                      ? <span className="selo selo--neutro">{x.status.toLowerCase()}</span>
                      : acabando
                        ? <span className="selo selo--atencao">acabando</span>
                        : <span className="selo selo--ok">ativo</span>}
                  </div>

                  {/* Duas faixas na mesma barra: o que já foi USADO e o
                      que está MARCADO mas ainda não aconteceu. Juntar as
                      duas num número só esconde exatamente a diferença
                      que decide se cabe remarcar. */}
                  <div className="pac__barra" role="img"
                       aria-label={`${usadas} de ${total} usadas, ${marcadas} marcadas`}>
                    <span className="pac__usado" style={{ width: `${pctUsado}%` }} />
                    <span className="pac__marcado" style={{ width: `${pctMarcado}%` }} />
                  </div>

                  <span className="ajuda">
                    {usadas} de {total} usadas
                    {marcadas > 0 && ` · ${marcadas} marcadas`}
                    {faltam > 0 ? ` · ${faltam} a marcar` : ' · nada a marcar'}
                    {x.validade && ` · vale até ${data(x.validade)}`}
                  </span>

                  {/* "Mas eu não vim naquele dia?" — a pergunta que o
                      saldo agregado não responde. O extrato fica
                      FECHADO por padrão: é conferência, não leitura de
                      todo dia, e aberto sempre viraria uma lista de dez
                      linhas por pacote empurrando o resto para baixo. */}
                  <ExtratoPacote pacotePacId={x.pacote_pac_id} />
                </li>
              )
            })}
          </ul>
        )}
      </Secao>

      {novo && (
        <NovoPacote pacienteId={pacienteId}
                    aoFechar={() => setNovo(false)}
                    aoCriar={async () => { setNovo(false); await carregar() }} />
      )}
    </>
  )
}

/**
 * O extrato de um pacote — sessão a sessão.
 *
 * A barra responde "quantas restam". Isto responde "quais foram,
 * quando e com quem", que é a pergunta que aparece no balcão quando o
 * paciente discorda do saldo. Sem ela, a recepção não tinha como
 * conferir e a discussão terminava em quem falava mais alto.
 *
 * Carrega SÓ ao abrir: são N linhas por pacote e a ficha já faz cinco
 * chamadas ao montar.
 */
function ExtratoPacote({ pacotePacId }: { pacotePacId: string }) {
  const [aberto, setAberto] = useState(false)
  const [itens, setItens] = useState<PacoteSessaoItem[] | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => {
    if (!aberto || itens) return
    void (async () => {
      try {
        const r = await api<{ itens: PacoteSessaoItem[] }>(
          `/v1/pacotes-vendidos/${pacotePacId}/sessoes`)
        setItens(r.itens)
      } catch (e) {
        setErro(e instanceof ErroApi ? e.message : 'Não foi possível abrir o extrato.')
      }
    })()
  }, [aberto, itens, pacotePacId])

  return (
    <>
      <button type="button" className="pac__trocar"
              onClick={() => setAberto(!aberto)} aria-expanded={aberto}>
        {aberto ? 'Fechar o extrato' : 'Ver sessão a sessão'}
      </button>

      {aberto && (
        erro ? <span className="ajuda">{erro}</span>
        : !itens ? <span className="ajuda">Carregando…</span>
        : (
          <ul className="pac__extrato">
            {itens.map((se) => (
              <li key={se.id} className="pac__ses">
                <span className="pac__ses-n">{se.ordem}</span>
                <span className="pac__ses-quando">
                  {se.agendamento_inicio ? (
                    <>
                      <strong>{data(se.agendamento_inicio)}</strong>
                      {se.profissional && ` · ${se.profissional}`}
                    </>
                  ) : (
                    <span className="ajuda">ainda não marcada</span>
                  )}
                </span>
                <SeloSessao status={se.status} />
              </li>
            ))}
          </ul>
        )
      )}
    </>
  )
}

/**
 * O estado de uma sessão, em palavra de gente.
 *
 * `NO_SHOW_CONSUMIDA` merece rótulo PRÓPRIO e cor própria: "queimou por
 * falta" e "usou" são a mesma baixa no saldo e histórias completamente
 * diferentes na hora de decidir se a clínica devolve a sessão. O código
 * gravava `CONSUMIDA` nos dois casos até 12/ago/2026.
 */
const SESSAO: Record<string, { rotulo: string; tom: string }> = {
  PREVISTA:          { rotulo: 'a marcar',   tom: 'neutro' },
  AGENDADA:          { rotulo: 'marcada',    tom: 'neutro' },
  CONSUMIDA:         { rotulo: 'usada',      tom: 'ok' },
  NO_SHOW_CONSUMIDA: { rotulo: 'faltou',     tom: 'erro' },
  EXPIRADA:          { rotulo: 'venceu',     tom: 'atencao' },
  ESTORNADA:         { rotulo: 'estornada',  tom: 'neutro' },
  CANCELADA:         { rotulo: 'cancelada',  tom: 'neutro' },
}

function SeloSessao({ status }: { status: string }) {
  const s = SESSAO[status] ?? { rotulo: status.toLowerCase(), tom: 'neutro' }
  return <span className={`selo selo--${s.tom}`}>{s.rotulo}</span>
}

/**
 * Vender um pacote ao paciente.
 *
 * ─── DUAS PORTAS, E A PRIMEIRA É A CERTA ──────────────────────────
 *
 * **Do catálogo** é o caminho normal: a clínica cadastrou "10 sessões
 * de fisioterapia, R$ 1.200, vale 120 dias" uma vez, em A clínica ›
 * Catálogo, e aqui é só escolher. Nome, sessões, preço e a regra de
 * no-show vêm de lá — e ficam FOTOGRAFADOS na venda, então reajustar o
 * catálogo depois não reescreve quem já comprou.
 *
 * **Avulso** continua existindo para o combinado que a clínica montou
 * fora do catálogo. Não é o padrão de propósito: era o único jeito até
 * agora, e é por isso que duas vendas do "mesmo" pacote saíam com
 * preços diferentes.
 */
function NovoPacote({ pacienteId, aoFechar, aoCriar }: {
  pacienteId: string; aoFechar(): void; aoCriar(): Promise<void>
}) {
  const [catalogo, setCatalogo] = useState<PacoteCatalogo[]>([])
  const [escolhido, setEscolhido] = useState('')
  const [avulso, setAvulso] = useState(false)
  const [nome, setNome] = useState('')
  const [sessoes, setSessoes] = useState('10')
  const [valor, setValor] = useState('')
  const [validade, setValidade] = useState('180')
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        const r = await api<{ itens: PacoteCatalogo[] }>('/v1/pacotes')
        setCatalogo(r.itens)
        if (r.itens.length === 1) setEscolhido(r.itens[0]!.id)
        // Catálogo vazio cai direto no avulso: mostrar uma lista sem
        // nada e um link para "cadastre primeiro" é fazer a recepção
        // sair da venda no meio de um atendimento.
        if (r.itens.length === 0) setAvulso(true)
      } catch { setAvulso(true) }
    })()
  }, [])

  /** "1.234,56" → 123456. O corpo da API é em CENTAVOS. */
  const centavos = (txt: string): number | null => {
    const limpo = txt.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.')
    const n = Number(limpo)
    return Number.isFinite(n) ? Math.round(n * 100) : null
  }

  const c = centavos(valor)
  const doCatalogo = catalogo.find((x) => x.id === escolhido) ?? null
  const pode = avulso
    ? (nome.trim().length >= 2 && Number(sessoes) >= 1 && c !== null && c >= 0)
    : !!escolhido

  async function salvar() {
    if (!pode) return
    setSalvando(true); setErro(null)
    try {
      await api(`/v1/pacientes/${pacienteId}/pacotes`, {
        metodo: 'POST',
        corpo: avulso
          ? {
              nome: nome.trim(),
              sessoes: Number(sessoes),
              valor_centavos: c,
              ...(Number(validade) > 0 ? { validade_dias: Number(validade) } : {}),
            }
          : { pacote_id: escolhido },
      })
      await aoCriar()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível criar o pacote.')
      setSalvando(false)
    }
  }

  return (
    <>
      <div className="painel__fundo" onClick={aoFechar} />
      <div className="porque" role="dialog" aria-modal="true" aria-label="Novo pacote">
        <h3>Novo pacote</h3>
        <p className="ajuda">
          O sistema desconta uma sessão a cada atendimento concluído. Não é preciso
          dar baixa à mão.
        </p>

        {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

        {!avulso && (
          <>
            <div className="campo">
              <label htmlFor="pc-cat">Qual pacote</label>
              <select id="pc-cat" className="entrada" value={escolhido}
                      onChange={(e) => setEscolhido(e.target.value)}>
                <option value="">Escolha…</option>
                {catalogo.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.nome} · {k.sessoes} sessões · {brl(k.valor_centavos)}
                  </option>
                ))}
              </select>
            </div>

            {/* O que a pessoa está prestes a vender, escrito antes de
                vender. Inclui a regra de no-show — é a que gera briga
                no balcão, e o paciente tem direito de ouvi-la agora. */}
            {doCatalogo && (
              <div className="pac__resumo">
                <p>
                  <strong>{doCatalogo.sessoes} sessões</strong> por{' '}
                  <strong>{brl(doCatalogo.valor_centavos)}</strong>
                  {' — '}{brl(Number(doCatalogo.valor_centavos) / doCatalogo.sessoes)} cada.
                </p>
                <p className="ajuda">
                  {doCatalogo.validade_dias
                    ? `Vale ${doCatalogo.validade_dias} dias a partir de hoje.`
                    : 'Sem prazo para usar.'}
                  {' '}
                  {doCatalogo.no_show_consome
                    ? 'Faltar sem avisar queima a sessão.'
                    : 'Faltar não queima a sessão — ela volta para a fila.'}
                </p>
              </div>
            )}
          </>
        )}

        {avulso && (
          <>
            <div className="campo">
              <label htmlFor="pc-nome">Nome</label>
              <input id="pc-nome" className="entrada" maxLength={120}
                     placeholder="10 sessões de reabilitação"
                     value={nome} onChange={(e) => setNome(e.target.value)} />
            </div>

            <div className="pac__campos">
              <div className="campo">
                <label htmlFor="pc-ses">Sessões</label>
                <input id="pc-ses" className="entrada" type="number" min={1} max={500}
                       value={sessoes} onChange={(e) => setSessoes(e.target.value)} />
              </div>
              <div className="campo">
                <label htmlFor="pc-val">Valor total</label>
                <input id="pc-val" className="entrada" inputMode="decimal"
                       placeholder="1.800,00"
                       value={valor} onChange={(e) => setValor(e.target.value)} />
              </div>
            </div>

            <div className="campo">
              <label htmlFor="pc-validade">Vale por (dias)</label>
              <input id="pc-validade" className="entrada" type="number" min={0} max={3650}
                     value={validade} onChange={(e) => setValidade(e.target.value)} />
              <span className="ajuda">
                0 = sem prazo. Prazo evita que uma sessão de dois anos atrás reapareça.
              </span>
            </div>
          </>
        )}

        {catalogo.length > 0 && (
          <button type="button" className="pac__trocar"
                  onClick={() => setAvulso(!avulso)}>
            {avulso
              ? '← Escolher um pacote do catálogo'
              : 'Montar um pacote fora do catálogo →'}
          </button>
        )}

        <div className="porque__acoes">
          <button className="botao botao--fantasma" onClick={aoFechar}>Cancelar</button>
          <button className="botao" disabled={!pode || salvando} onClick={() => void salvar()}>
            {salvando ? 'Vendendo…' : 'Vender pacote'}
          </button>
        </div>
      </div>
    </>
  )
}
